import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Building,
  Check,
  Eye,
  EyeOff,
  Globe,
  Lock,
  Mail,
  MapPin,
  Phone,
  User
} from 'lucide-react';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import countryCodes from './countryCodes.json'; // You will need to add a static countryCodes.json file

const Register = () => {
  const { register, loading, error } = useAuth();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    // Company Information
    companyName: '',
    companyType: '',
    companySize: '',
    industry: '',
    website: '',
    description: '',
    
    // Address
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    
    // Contact Person
    firstName: '',
    lastName: '',
    jobTitle: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    countryCode: '+1' // default to US
  });

  const companyTypes = [
    'Startup',
    'Private Company',
    'Public Company',
    'Non-Profit',
    'Government',
    'Consulting',
    'Agency'
  ];

  const companySizes = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1000+'
  ];

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'Retail',
    'Manufacturing',
    'Consulting',
    'Real Estate',
    'Marketing',
    'Other'
  ];

  const steps = [
    { id: 1, name: 'Company', description: 'Company Information' },
    { id: 2, name: 'Address', description: 'Company Address' },
    { id: 3, name: 'Contact', description: 'Contact Person' },
    { id: 4, name: 'Account', description: 'Account Setup' }
  ];

  const nameRegex = /^[A-Za-z\s'-]+$/;
  const phoneRegex = /^\d{10}$/;
  const zipRegex = /^[A-Za-z0-9\-\s]{3,10}$/; // Accepts 3-10 alphanumeric, dash, space

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear specific error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const handleCountryCodeChange = (e) => {
    setFormData({
      ...formData,
      countryCode: e.target.value
    });
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.companyName) newErrors.companyName = 'Company name is required';
        if (!formData.companyType) newErrors.companyType = 'Company type is required';
        if (!formData.companySize) newErrors.companySize = 'Company size is required';
        if (!formData.industry) newErrors.industry = 'Industry is required';
        if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
          newErrors.website = 'Please enter a valid URL';
        }
        break;
      case 2:
        if (!formData.address) newErrors.address = 'Address is required';
        if (!formData.city) newErrors.city = 'City is required';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.country) newErrors.country = 'Country is required';
        if (formData.postalCode && !zipRegex.test(formData.postalCode)) {
          newErrors.postalCode = 'Postal code is invalid';
        }
        break;
      case 3:
        if (!formData.firstName) {
          newErrors.firstName = 'First name is required';
        } else if (!nameRegex.test(formData.firstName)) {
          newErrors.firstName = 'First name must only contain letters, spaces, apostrophes, or hyphens';
        }
        if (!formData.lastName) {
          newErrors.lastName = 'Last name is required';
        } else if (!nameRegex.test(formData.lastName)) {
          newErrors.lastName = 'Last name must only contain letters, spaces, apostrophes, or hyphens';
        }
        if (!formData.jobTitle) newErrors.jobTitle = 'Job title is required';
        if (!formData.phone) {
          newErrors.phone = 'Phone number is required';
        } else if (!phoneRegex.test(formData.phone)) {
          newErrors.phone = 'Phone number must be exactly 10 digits';
        }
        break;
      case 4:
        if (!formData.email) {
          newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = 'Email is invalid';
        }
        if (!formData.password) {
          newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
          newErrors.password = 'Password must be at least 6 characters';
        }
        if (!formData.confirmPassword) {
          newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(4)) return;

    // Build payload to match backend validation schema
    const payload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      password: formData.password,
      phone: `${formData.countryCode}${formData.phone}`.replace(/\s/g, ''),
      jobTitle: formData.jobTitle.trim(),
      experience: 1, // default 1 year; adjust if you add input later
      company: {
        name: formData.companyName.trim(),
      industry: formData.industry,
        size: formData.companySize,
        type: formData.companyType,
      website: formData.website,
      description: formData.description,
        address: {
          street: formData.address,
      city: formData.city,
      state: formData.state,
      country: formData.country,
          postalCode: formData.postalCode
        }
      },
      specializations: [formData.industry]
    };

    const result = await register(payload);

    if (result.success) {
      navigate('/dashboard');
    } else {
      // Try to parse field errors if backend provides them
      if (result.data && result.data.errors) {
        // If backend returns errors as an object with field keys
        setErrors(result.data.errors);
      } else if (result.message) {
        // Show toast for general error
        if (window.toast) {
          window.toast.error(result.message);
        }
      }
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="form-group">
              <label className="form-label">Company Name</label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className={`input-field pl-10 ${errors.companyName ? 'border-red-300' : ''}`}
                  placeholder="Enter company name"
                />
              </div>
              {errors.companyName && <p className="mt-2 text-sm text-red-600">{errors.companyName}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label">Company Type</label>
                <select
                  name="companyType"
                  value={formData.companyType}
                  onChange={handleChange}
                  className={`input-field ${errors.companyType ? 'border-red-300' : ''}`}
                >
                  <option value="">Select company type</option>
                  {companyTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {errors.companyType && <p className="mt-2 text-sm text-red-600">{errors.companyType}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">Company Size</label>
                <select
                  name="companySize"
                  value={formData.companySize}
                  onChange={handleChange}
                  className={`input-field ${errors.companySize ? 'border-red-300' : ''}`}
                >
                  <option value="">Select company size</option>
                  {companySizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                {errors.companySize && <p className="mt-2 text-sm text-red-600">{errors.companySize}</p>}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Industry</label>
              <select
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                className={`input-field ${errors.industry ? 'border-red-300' : ''}`}
              >
                <option value="">Select industry</option>
                {industries.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
              {errors.industry && <p className="mt-2 text-sm text-red-600">{errors.industry}</p>}
            </div>

            <div className="form-group">
              <label className="form-label">Website (Optional)</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className={`input-field pl-10 ${errors.website ? 'border-red-300' : ''}`}
                  placeholder="https://www.company.com"
                />
              </div>
              {errors.website && <p className="mt-2 text-sm text-red-600">{errors.website}</p>}
            </div>

            <div className="form-group">
              <label className="form-label">Company Description (Optional)</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="input-field"
                placeholder="Brief description of your company"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="form-group">
              <label className="form-label">Address</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows={3}
                  className={`input-field pl-10 ${errors.address ? 'border-red-300' : ''}`}
                  placeholder="Enter company address"
                />
              </div>
              {errors.address && <p className="mt-2 text-sm text-red-600">{errors.address}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={`input-field ${errors.city ? 'border-red-300' : ''}`}
                  placeholder="Enter city"
                />
                {errors.city && <p className="mt-2 text-sm text-red-600">{errors.city}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">State/Province</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className={`input-field ${errors.state ? 'border-red-300' : ''}`}
                  placeholder="Enter state/province"
                />
                {errors.state && <p className="mt-2 text-sm text-red-600">{errors.state}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label">Country</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={`input-field ${errors.country ? 'border-red-300' : ''}`}
                  placeholder="Enter country"
                />
                {errors.country && <p className="mt-2 text-sm text-red-600">{errors.country}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">Postal Code (Optional)</label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className={`input-field ${errors.postalCode ? 'border-red-300' : ''}`}
                  placeholder="Enter postal code"
                />
                {errors.postalCode && <p className="mt-2 text-sm text-red-600">{errors.postalCode}</p>}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label">First Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`input-field pl-10 ${errors.firstName ? 'border-red-300' : ''}`}
                    placeholder="Enter first name"
                  />
                </div>
                {errors.firstName && <p className="mt-2 text-sm text-red-600">{errors.firstName}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`input-field ${errors.lastName ? 'border-red-300' : ''}`}
                  placeholder="Enter last name"
                />
                {errors.lastName && <p className="mt-2 text-sm text-red-600">{errors.lastName}</p>}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Job Title</label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleChange}
                  className={`input-field pl-10 ${errors.jobTitle ? 'border-red-300' : ''}`}
                  placeholder="e.g. HR Manager, Talent Acquisition"
                />
              </div>
              {errors.jobTitle && <p className="mt-2 text-sm text-red-600">{errors.jobTitle}</p>}
            </div>

            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <div className="flex">
                <div className="w-2/6 mr-2">
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleCountryCodeChange}
                    className="input-field w-full"
                  >
                    {countryCodes.map((c) => (
                      <option key={c.code} value={c.dial_code}>{c.name} ({c.dial_code})</option>
                    ))}
                  </select>
                </div>
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`input-field pl-10 ${errors.phone ? 'border-red-300' : ''}`}
                    placeholder="Enter 10 digit phone number"
                    maxLength={10}
                  />
                </div>
              </div>
              {errors.phone && <p className="mt-2 text-sm text-red-600">{errors.phone}</p>}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`input-field pl-10 ${errors.email ? 'border-red-300' : ''}`}
                  placeholder="Enter email address"
                />
              </div>
              {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`input-field pl-10 pr-10 ${errors.password ? 'border-red-300' : ''}`}
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
              {errors.password && <p className="mt-2 text-sm text-red-600">{errors.password}</p>}
            </div>

            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`input-field pl-10 pr-10 ${errors.confirmPassword ? 'border-red-300' : ''}`}
                  placeholder="Confirm password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 gradient-bg-primary rounded-2xl flex items-center justify-center mx-auto shadow-lg">
            <Briefcase className="w-8 h-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold gradient-text">
            Create Recruiter Account
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Join jobzentro and start finding the best talent
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  currentStep >= step.id 
                    ? 'bg-blue-600 border-blue-600 text-white' 
                    : 'border-gray-300 text-gray-400'
                }`}>
                  {currentStep > step.id ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <span className="text-sm font-medium">{step.id}</span>
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-full h-0.5 mx-4 ${
                    currentStep > step.id ? 'bg-blue-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            {steps.map((step) => (
              <div key={step.id} className="text-xs text-center">
                <p className="font-medium text-gray-900">{step.name}</p>
                <p className="text-gray-500">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-luxury p-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Error summary at the top */}
            {Object.keys(errors).length > 0 && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                <ul className="list-disc pl-5">
                  {Object.entries(errors).map(([field, msg]) => (
                    <li key={field}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStepContent()}
            </motion.div>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="btn-secondary flex items-center"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Previous
                </button>
              ) : (
                <div></div>
              )}

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  data-testid="next-btn"
                  className="btn-primary flex items-center"
                >
                  Next
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-btn"
                  className="btn-primary flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="loading-spinner mr-2"></div>
                      Creating Account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
              )}
            </div>
          </form>

          {/* Login Link */}
          <div className="text-center mt-6">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Register; 